import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext, Store } from '@ngxs/store'
import { sortBy, uniqBy } from 'lodash'
import { tap } from 'rxjs/operators'
import { ShopsState } from '@lla-platform/shops/shops-data-access'
import { TargetsService } from '../targets.service'
import {
  IPeriodTargetDetailsResponse,
  IPeriodTargetResponse
} from '../../interfaces/weekly-target.interface'
import { GetShopsTargetsByPeriod, GetShopsTargetsByPeriodDetails } from './period-target.actions'
import { PeriodTargetStateModel } from './period-target.model'

@State<PeriodTargetStateModel>({
  name: 'periodTarget'
})
@Injectable()
export class PeriodTargetState {
  constructor(
    private targetsService: TargetsService,
    private store: Store
  ) {}

  @Selector()
  static periodTargets(state: PeriodTargetStateModel): IPeriodTargetResponse | undefined {
    return state.periodTargets
      ? {
          currentPeriod: state.periodTargets?.currentPeriod,
          dates: state.periodTargets?.dates,
          locations: state.periodTargets?.locations?.map((el) => ({
            ...el,
            items: el.items?.map((item) => ({
              ...item,
              locationName: el.location?.name,
              locationType: el.location?.locationType
            }))
          }))
        }
      : undefined
  }

  @Selector()
  static periodTargetDetails(state: PeriodTargetStateModel): IPeriodTargetDetailsResponse | undefined {
    return state.periodTargetDetails
  }

  @Selector()
  static allDivisions(state: PeriodTargetStateModel) {
    const data = state.periodTargets?.locations ?? []
    return [
      { label: 'All', value: 'all' },
      ...sortBy(
        uniqBy(
          data
            .map((el) => ({
              label: el.locationInfo?.division ?? '',
              value: el.locationInfo?.division ?? ''
            }))
            .filter((el) => el.value !== ''),
          (x) => x.value
        ),
        (x) => x.label
      )
    ]
  }

  @Action(GetShopsTargetsByPeriod)
  getShopsTargetsByPeriod(
    ctx: StateContext<PeriodTargetStateModel>,
    { payload }: GetShopsTargetsByPeriod
  ) {
    ctx.patchState({ periodTargets: undefined })
    return this.targetsService.getShopsTargetsByPeriod(payload).pipe(
      tap((res) => {
        const allShops = this.store.selectSnapshot(ShopsState.allShops)
        ctx.patchState({
          periodTargets: {
            currentPeriod: res?.currentPeriod,
            dates: res?.dates ?? [],
            locations: (res?.locations ?? []).map((el) => ({
              ...el,
              locationInfo: allShops.find((s) => s.id === el.location?.id)
            }))
          }
        })
      })
    )
  }

  @Action(GetShopsTargetsByPeriodDetails)
  getShopsTargetsByPeriodDetails(
    ctx: StateContext<PeriodTargetStateModel>,
    { payload, locationIds }: GetShopsTargetsByPeriodDetails
  ) {
    ctx.patchState({ periodTargetDetails: undefined })
    return this.targetsService.getShopsTargetsByPeriodDetails(payload, locationIds).pipe(
      tap((res) => {
        ctx.patchState({ periodTargetDetails: res })
      })
    )
  }
}
